import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { AuthService } from "src/app/api/auth.service";
import { AccountType } from "src/app/constants/account-types";
import { LangService } from "../lang.service";
import { first } from "rxjs/operators";
import { SS_MFA_SETUP_KEY } from "../login-ctrl";
import { accessDeniedQueryParams } from "./access-denied";

@Injectable({ providedIn: 'root' })
export class GlobalAuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private auth: AuthService,
    private lang: LangService
  ) {}

  /**
   * Doesn't immediately block access to the route, instead:
   *  - Subscribes to the userSub and waits for first userInfo to be emitted
   *  - Checks if the user has access to the route/subroute based on their account types
   *  - If the user doesn't have access, they are redirected to the login page, carrying the role the
   *    route needs and the page they asked for so that page can explain (CONF-001)
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const allowedRoles = route.data['allowedRoles'] as AccountType[] | undefined;

    console.log('canActivate()', allowedRoles);

    if(!allowedRoles || allowedRoles.length === 0) {
      return true; // no roles specified, allow access to all
    }

    this.checkAccess(allowedRoles, state.url);

    return true;
  }

  checkAccess(allowedRoles: AccountType[], attemptedUrl?: string) {
    this.auth.userSub.pipe(first(user => !!user)).subscribe(user => {
      const hasAccess = this.auth.isLoginAs || allowedRoles.some(requiredRole => {
        return user.accountTypes.some(userRole => {
          return userRole.account_type === requiredRole;
        });
      });

      // check mfa-status, using sessionStorage or api call or check login-ctrl's subscription? Would require dependency and service injection - login-ctrl class not set-up this way
      const isMfaMissing = sessionStorage.getItem(SS_MFA_SETUP_KEY) === 'false';
      
      console.log(`checkAccess(${allowedRoles})`, hasAccess);
      if (!hasAccess) {
        // Signed in, but not with a role this route accepts: say so, and remember where they were going.
        this.router.navigate([this.lang.c(), 'login-router-st'], {
          queryParams: accessDeniedQueryParams(allowedRoles, attemptedUrl),
        });
      }
      else if (isMfaMissing) {
        this.router.navigate([this.lang.c(), 'login-router-st']);
      }
      
    });
  }
}

/**
 * Checks if the user has access to the route/subroute based on their account types
 * @param allowedRoles: AccountType[] - The roles that are allowed to access the route
 */
export const allowOnlyRoles = (allowedRoles: AccountType[]) => {
  return {
    canActivate: [GlobalAuthGuard],
    data: {
      allowedRoles
    }
  }
};
